import React from 'react'
import { getElementCenterPosition } from '@edulastic/common/src/utils/html'
import * as Sentry from '@sentry/browser'

export const ALPHABET = [
  'A',
  'B',
  'C',
  'D',
  'E',
  'F',
  'G',
  'H',
  'I',
  'J',
  'K',
  'L',
  'M',
  'N',
  'O',
  'P',
  'Q',
  'R',
  'S',
  'T',
  'U',
  'V',
  'W',
  'X',
  'Y',
  'Z',
]
export const APP_NAME = 'Edulastic'
export const QUESTION_OPTIONS = 'QUESTION_OPTIONS'
export const QUESTION_PROBLEM = 'QUESTION_PROBLEM'
export const QUESTION_ANSWERS = 'QUESTION_ANSWERS'
export const QUESTION_USERSELECTIONS = 'QUESTION_USERSELECTIONS'
export const ASSESSMENTID = 'AssessmentId'
export const LARGE_DESKTOP_WIDTH = 1600
export const MEDIUM_DESKTOP_WIDTH = 1300
export const SMALL_DESKTOP_WIDTH = 993
export const NORMAL_MONITOR_WIDTH = 1366
export const MAX_XGA_WIDTH = 1024
export const MAX_TAB_WIDTH = 768
export const MAX_MOBILE_WIDTH = 468

export const DATA_STUDIO_DISABLED_DISTRICTS = [
  '635918f1559a0f00082f0694',
  '64929037eec3b3a15fb8dac8',
  '631f286ca526d5198f1eba80',
]

export const SWITCH_ACCOUNT_TUTORIAL_STEP = [
  {
    target: '[data-joyride-id="switchUserNew"]',
    title: 'Manage Your Accounts Easily',
    content:
      'You can now see the total number of connected accounts in your switch account menu.',
    placement: 'right',
    disableBeacon: true,
  },
]
export const SWITCH_ACCOUNT_TUTORIAL_STEP_ADD_ACCOUNT_USER = [
  {
    target: '[data-joyride-id="switchUserNew"]',
    title: 'New Account Successfully Linked',
    content: 'A new account has been added to your switch account menu.',
    placement: 'right',
    disableBeacon: true,
  },
]

export const PEARY_AI_TUTORIAL_STEP = [
  {
    target: '[data-joyride-id="pearyAi"]',
    title: '👋 Meet Peary',
    content:
      'Your AI assistant to help you explore features, create assessments, and work faster.',
    placement: 'right',
    disableBeacon: true,
    disableScrollParentFix: true,
  },
]

export const TEACHER_COMMON_TUTORIAL_STEP = [
  {
    target: '[data-joyride-id="manageDistrict"]',
    title: 'New Common Test Setting',
    content:
      'You can now let selected teachers create common test, without admin intervention.',
    placement: 'right',
    disableBeacon: true,
  },
  {
    target: '[data-joyride-id="teacherCommonSetting"]',
    title: 'New Common Test Setting',
    content:
      'Selecting ‘Yes’ allows teachers to choose common test types. Admins can manage which teachers can publish district and school common tests.',
    placement: 'right',
    disableBeacon: true,
  },
]

export const ITEM_DETAILS_CHANGE_QUESTION_TYPE_TUTORIAL_STEPS = (
  showInlineMathEssayGuidance = false
) => [
  {
    target: '[data-joyride-id="changeQuestionType"]',
    title: 'Change Item Type',
    content: showInlineMathEssayGuidance
      ? 'Essay with Rich Text, Essay with Plain Text, and Math Essay items can now be converted to Inline Math Essay. Select the pencil next to the item type; the question, standards, and rubric will carry over.'
      : 'You can switch this item to a different item type. The question, standards, and rubric will carry over.',
    placement: 'bottom-start',
    disableBeacon: true,
  },
]

export const ASSIGNMENT_SETTINGS_CHANGES_TUTORIAL_STEPS = [
  {
    target: '[data-joyride-id="testBehaviourTab"]',
    title: (
      <div>
        New Additions in
        <br />
        Test Behavior
      </div>
    ),
    content:
      'Test Behavior now includes Answer On Paper and Choose Test Interface previously under Miscellaneous.',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-joyride-id="accommodationsTab"]',
    title: 'Introducing Accommodation',
    content: 'Now easily access accessibility and accommodations settings',
    placement: 'bottom',
    disableBeacon: true,
  },
  {
    target: '[data-joyride-id="performanceBandTab"]',
    title: 'Performance Band',
    content:
      'Choose from the Performance Bands & Standard based grading scale set by your district.',
    placement: 'bottom',
    disableBeacon: true,
  },
]

export const TEST_INSTRUCTIONS_MULTILANGUAGE_TUTORIAL_STEPS = [
  {
    target: `[data-joyride-id="testInstructionsToggle"]`,
    title: 'Multi language Support',
    content:
      'Deliver test instructions in multiple languages with TTS. Add an optional acknowledgment checkbox for students before starting an assignment.',
    placement: 'left',
    disableBeacon: true,
    styles: {
      tooltip: {
        width: '256px',
      },
    },
  },
  {
    target: `[data-joyride-id="testInstructions"]`,
    title: 'Multi language Test Instructions & Customize TTS',
    content:
      'Add Test Instructions in supported languages with TTS. Students will receive the instructions and audio based on their language & TTS settings.',
    placement: 'left',
    disableBeacon: true,
    styles: {
      tooltip: {
        width: '270px',
      },
    },
  },
  {
    target: `[data-joyride-id="testInstructions"]`,
    title: 'Student Acknowledgement',
    content:
      'Choose "Show to students" to require students to acknowledge Test Instructions before starting the assignment.',
    placement: 'left',
    disableBeacon: true,
  },
]

const getFloaterCoordinatesForTarget = (targetElement, floater) => {
  try {
    const { y: targetPosition } = getElementCenterPosition(targetElement) || {}

    const transform = floater.style.transform

    if (!targetPosition || !transform) return null

    const floaterCoordinates = transform.match(/([\d.]+)px/g)
    const floaterDimensions = floater.getBoundingClientRect()
    if (!floaterCoordinates) return null

    return {
      transform: `translate3d(${floaterCoordinates[0] || '0px'}, ${
        targetPosition - floaterDimensions.height / 2
      }px, ${floaterCoordinates[2] || '0px'})`,
    }
  } catch (err) {
    Sentry.captureException(err)
  }
}

export const getTestInstructionsMultilanguageTutorialStepStyles = (
  stepIndex,
  hasInstructions,
  floater
) => {
  let style = {}
  if (hasInstructions) {
    if (stepIndex === 0) {
      const transformStyle = getFloaterCoordinatesForTarget(
        document.getElementById('test-instructions-description'),
        floater
      )

      style = transformStyle || {
        top: '-108px',
      }
    }
    if (stepIndex === 1) {
      const transformStyle = getFloaterCoordinatesForTarget(
        document.getElementById('test-instructions-label'),
        floater
      )

      style = transformStyle || {
        top: '0px',
      }
    }
    if (stepIndex === 2) {
      const transformStyle = getFloaterCoordinatesForTarget(
        document.getElementById('test-instructions-acknowledgement'),
        floater
      )

      style = transformStyle || {
        top: '80px',
      }
    }
  }
  return style
}
